import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import DocUpload from "@/components/DocUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtDateTime } from "@/components/DataTable";
import { toast } from "sonner";
import { Download, Search, Pencil, RefreshCw, Archive, FileText, History, AlertTriangle, RotateCcw, ShieldCheck } from "lucide-react";

const fmtKB = (b) => (!b ? "" : b < 1024 ? "<1 KB" : `${Math.round(b / 1024)} KB`);

export default function FilingCabinet({ partyId, policies = [], canWrite, canArchive }) {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [fcat, setFcat] = useState("");
  const [edit, setEdit] = useState(null);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [view, setView] = useState("active");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    const params = { party_id: partyId, archived: view };
    if (q) params.q = q;
    if (fcat) params.category = fcat;
    try {
      const { data } = await api.get("/dropbox/documents", { params });
      setDocs(data.items || []);
    } catch (e) {
      setError(true); setDocs([]);
    } finally { setLoading(false); }
  }, [partyId, q, fcat, view]);

  useEffect(() => { api.get("/dropbox/documents/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, []);
  useEffect(() => { if (partyId) load(); }, [load]);

  const download = async (d) => {
    try { const { data } = await api.get(`/dropbox/documents/${d.id}/link`); window.open(data.link, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open."); }
  };
  const downloadVersion = async (d, version) => {
    try { const { data } = await api.get(`/dropbox/documents/${d.id}/version-link`, { params: { version } }); window.open(data.link, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open."); }
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/dropbox/documents/${edit.id}`, { filename: edit.filename, category: edit.category, notes: edit.notes });
      toast.success("Document updated."); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); }
  };
  const doReplace = async () => {
    if (!replaceFile) { toast.error("Choose a file."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", replaceFile);
      await api.post(`/dropbox/documents/${replaceDoc.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version saved (prior version retained)."); setReplaceDoc(null); setReplaceFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } finally { setBusy(false); }
  };
  const archive = async (d) => {
    if (!window.confirm(`Archive "${d.filename}"?`)) return;
    try { await api.post(`/dropbox/documents/${d.id}/archive`); toast.success("Archived."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const restore = async (d) => {
    try { await api.post(`/dropbox/documents/${d.id}/restore`); toast.success("Document restored to the active cabinet."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const archivedView = view === "archived";

  return (
    <div data-testid="filing-cabinet">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {canWrite && !archivedView && <DocUpload partyId={partyId} policies={policies} onSaved={load} />}
        <div className="inline-flex rounded-sm border border-border overflow-hidden">
          <button data-testid="doc-view-active" onClick={() => setView("active")}
            className={`px-3 h-9 text-sm ${!archivedView ? "bg-navy text-white" : "bg-white text-slate"}`}>Active</button>
          <button data-testid="doc-view-archived" onClick={() => setView("archived")}
            className={`px-3 h-9 text-sm ${archivedView ? "bg-navy text-white" : "bg-white text-slate"}`}>Archived</button>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-mutedsea" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="pl-8 h-9" />
        </div>
        <Select value={fcat || "all"} onValueChange={(v) => setFcat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52 h-9" data-testid="doc-filter-cat"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-mutedsea py-8 text-center" data-testid="doc-loading">Loading documents…</p>
      ) : error ? (
        <div className="text-sm text-seared py-8 text-center flex flex-col items-center gap-2" data-testid="doc-error">
          <AlertTriangle className="h-5 w-5" /> Could not load the filing cabinet.
          <Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-mutedsea py-8 text-center">{archivedView ? "No archived documents." : "No documents filed yet."}</p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            return (
              <div key={d.id} className="bg-white border border-border rounded-sm p-3 flex items-center gap-3" data-testid={`doc-${d.id}`}>
                <FileText className="h-5 w-5 text-atlantic shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate truncate">{d.filename}</span>
                    <Badge variant="outline" className="bg-cloud text-slate border-border">{d.category}</Badge>
                    {archivedView && <Badge variant="outline" className="bg-seared/10 text-seared border-seared/30" data-testid={`doc-archived-badge-${d.id}`}>Archived</Badge>}
                    {d.entry_hash && <ShieldCheck data-testid={`doc-seal-${d.id}`} className="h-3.5 w-3.5 text-seagreen" title={`Sealed ${d.sealed_at || ""} · ${String(d.entry_hash).slice(0, 12)}…`} />}
                    {d.version && d.version !== "v1" && (
                      <button data-testid={`doc-history-${d.id}`} onClick={() => setHistoryDoc(d)}
                        className="inline-flex items-center rounded-sm border border-atlantic/30 bg-atlantic/10 text-atlantic text-xs px-2 py-0.5 hover:bg-atlantic/20">
                        <History className="h-3 w-3 mr-1" />{d.version} · history
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-mutedsea mt-0.5">{fmtDate(d.created_at)} · {d.created_by || "—"}{d.file_size ? ` · ${fmtKB(d.file_size)}` : ""}{d.notes ? ` · ${d.notes}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" title="Download" data-testid={`doc-dl-${d.id}`} onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                  {archivedView ? (
                    canArchive && <Button size="icon" variant="ghost" title="Restore to active" data-testid={`doc-restore-${d.id}`} onClick={() => restore(d)}><RotateCcw className="h-4 w-4 text-seagreen" /></Button>
                  ) : (
                    <>
                      {canWrite && <Button size="icon" variant="ghost" title="Rename / move / describe" data-testid={`doc-edit-${d.id}`} onClick={() => setEdit({ ...d })}><Pencil className="h-4 w-4" /></Button>}
                      {canWrite && <Button size="icon" variant="ghost" title="Replace (new version)" data-testid={`doc-replace-${d.id}`} onClick={() => setReplaceDoc(d)}><RefreshCw className="h-4 w-4" /></Button>}
                      {canArchive && <Button size="icon" variant="ghost" title="Archive" data-testid={`doc-archive-${d.id}`} onClick={() => archive(d)}><Archive className="h-4 w-4" /></Button>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="doc-edit-modal">
          <DialogHeader>
            <DialogTitle>Document details</DialogTitle>
            <DialogDescription>Rename, move to another category, or add a description.</DialogDescription>
          </DialogHeader>
          {edit && <div className="space-y-3">
            <div><Label className="text-xs mb-1 block">Filename</Label><Input value={edit.filename || ""} onChange={(e) => setEdit({ ...edit, filename: e.target.value })} /></div>
            <div><Label className="text-xs mb-1 block">Category / folder</Label>
              <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                <SelectTrigger data-testid="doc-edit-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs mb-1 block">Description / notes</Label><Input value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button data-testid="doc-edit-save" onClick={saveEdit} disabled={busy}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replaceDoc} onOpenChange={(o) => !o && setReplaceDoc(null)}>
        <DialogContent data-testid="doc-replace-modal">
          <DialogHeader>
            <DialogTitle>Replace document (keeps prior version)</DialogTitle>
            <DialogDescription>The current version is preserved in history; the new file becomes current. The filename you set is kept.</DialogDescription>
          </DialogHeader>
          <Input type="file" data-testid="doc-replace-file" onChange={(e) => setReplaceFile((e.target.files || [])[0])} />
          <DialogFooter><Button variant="outline" onClick={() => setReplaceDoc(null)}>Cancel</Button><Button data-testid="doc-replace-save" onClick={doReplace} disabled={busy}>Upload new version</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyDoc} onOpenChange={(o) => !o && setHistoryDoc(null)}>
        <DialogContent data-testid="doc-history-modal">
          <DialogHeader>
            <DialogTitle>Version history — {historyDoc?.filename}</DialogTitle>
            <DialogDescription>Prior versions are retained and can be downloaded.</DialogDescription>
          </DialogHeader>
          {historyDoc && (
            <div className="space-y-2">
              <div className="flex items-center justify-between border border-border rounded-sm p-2 bg-cloud"
                   data-testid={`doc-version-${historyDoc.version}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate">{historyDoc.version} (current)</span>
                  <span className="text-xs text-mutedsea ml-2">{fmtKB(historyDoc.file_size)}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadVersion(historyDoc, historyDoc.version)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
              </div>
              {[...(historyDoc.versions || [])].reverse().map((v, i) => (
                <div key={i} className="flex items-center justify-between border border-border rounded-sm p-2" data-testid={`doc-version-${v.version}`}>
                  <div className="text-sm">
                    <span className="font-medium text-slate">{v.version}</span>
                    <span className="text-xs text-mutedsea ml-2">{v.replaced_at ? `replaced ${fmtDateTime(v.replaced_at)}` : ""}{v.replaced_by ? ` · ${v.replaced_by}` : ""}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadVersion(historyDoc, v.version)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistoryDoc(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
